import { useCallback, useEffect } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { L } from '../../../../redux/List/reducer';
import {useListManager} from "../../../../redux/List/helper";
import ContactActions from 'bukku/redux/Contact/actions';
import { usePrevious } from '../../../../helpers/useReadStateSuccess';
import {Type} from "bukku/redux/Transaction/helper";

const {
    setBillingContactPersonId,
    setShippingContactPersonId,
} = ContactActions;

const selectorFn = state => ({
    contactPersons: state.List.contactPersons,
    contactId: state.Transaction.contactId,
    transaction: state.Transaction.transaction,
    transactionInitState: state.Transaction.transactionInitState,
});

const getDisplayName = cp => {
    let displayName;
    if (cp.first_name) {
        if (cp.last_name)
            displayName = `${cp.first_name} ${cp.last_name}`;

        else
            displayName = cp.first_name;
    }
    else if (cp.last_name) {
        displayName = cp.last_name;
    }

    return displayName;
};

export function useContactPersons({ form, specs }) {
    const listManagerContactPersons = useListManager([L.contact_persons]);
    const dispatch = useDispatch();

    // Redux
    const {
        transaction,
        contactPersons,
        transactionInitState,
    } = useSelector(selectorFn, shallowEqual);

    const prevInitState = usePrevious(transactionInitState);

    // On contact id changed, find and set the new contact's default billing and shipping cp
    useEffect(() => {
        // (prevInitState === 'success') is so that we don't update the CP again here when
        // initialising the transaction (state = 'request').  That will be done in updateStore.js and updateForm.js
        if (prevInitState === 'success' && listManagerContactPersons.onListReady()) {
            const billingContactPerson = contactPersons.find(cp => cp.is_default_billing);
            const shippingContactPerson =
            (
                transaction.type !== Type.purchase_bill &&
                transaction.type !== Type.purchase_credit_note &&
                transaction.type !== Type.purchase_goods_received_note &&
                transaction.type !== Type.purchase_order
            ) ?
                contactPersons.find(cp => cp.is_default_shipping) : {};

            // Billing
            form.setFieldsValue({
                billing_contact_person_id_or_name: billingContactPerson ?
                    getDisplayName(billingContactPerson) : null,
            });
            dispatch(setBillingContactPersonId(billingContactPerson?.id));

            // Shipping
            form.setFieldsValue({
                shipping_contact_person_id_or_name: shippingContactPerson ?
                    getDisplayName(shippingContactPerson) : null,
            });

            dispatch(setShippingContactPersonId(shippingContactPerson?.id));
        }
    }, [transaction, listManagerContactPersons, contactPersons, dispatch, form, prevInitState]);

    const readContactPersons = useCallback(contactId => {
        if (!contactId)
            return null;
        if (specs.contactPersons)
            listManagerContactPersons.readLists({ contact_persons: { contact_id: contactId } });

        return null;
    }, [
        listManagerContactPersons, specs.contactPersons,
    ]);

    return {
        readContactPersons,
    };
}
